import React from 'react';
import { ResponsiveModal } from '../../ui/ResponsiveModal';
import { FileText, Upload, Loader, X } from 'lucide-react';

interface UnsignedContractUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: () => Promise<void>;
  contractFile: File | null;
  uploading: boolean;
  dragActive: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onRemoveFile: () => void;
}

const UnsignedContractUploadModal: React.FC<UnsignedContractUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  contractFile,
  uploading,
  dragActive,
  fileInputRef,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveFile
}) => {
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Carica Proposta Personalizzata"
      size="md"
    >
      <div className="space-y-4">
        <p className="text-black/70 dark:text-white/70">
          Carica una proposta creata o modificata al di fuori del CRM.
          Questo file sarà disponibile per il download ma non cambierà lo stato dell'offerta.
        </p>

        <div
          className={`border-2 border-dashed ${dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-black/20 dark:border-gray-600'} rounded-lg p-6 text-center transition-colors duration-200 bg-white/50 dark:bg-gray-800/50`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onDragEnter={onDragOver}
        >
          <input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            onChange={onFileChange}
            className="hidden"
          />
          {contractFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center">
                <FileText className="h-10 w-10 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-sm font-medium text-black dark:text-white">{contractFile.name}</p>
              <p className="text-xs text-black/60 dark:text-white/60">
                {(contractFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <button
                type="button"
                onClick={onRemoveFile}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
              >
                Rimuovi
              </button>
            </div>
          ) : (
            <div
              className="space-y-2 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex items-center justify-center">
                <Upload className={`h-10 w-10 ${dragActive ? 'text-blue-500 dark:text-blue-400' : 'text-black/40 dark:text-white/40'}`} />
              </div>
              <p className="text-sm font-medium text-black dark:text-white">Seleziona un file</p>
              <p className="text-xs text-black/60 dark:text-white/60">oppure trascina e rilascia</p>
              <p className="text-xs text-black/40 dark:text-white/40">Solo file PDF (max 10MB)</p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-black dark:text-white glass-card rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={!contractFile || uploading}
            className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
              !contractFile || uploading
                ? 'bg-blue-500/50 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {uploading ? (
              <>
                <span className="mr-2">Caricamento...</span>
                <Loader className="h-4 w-4 inline animate-spin" />
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2 inline" />
                Carica Proposta
              </>
            )}
          </button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default UnsignedContractUploadModal;
